import { apiClient, getErrorMessage } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import {
  API_EDI_LINE_DISPLAY_PROFILE_RESOLVE,
  API_EDI_LINE_DISPLAY_PROFILE_LIST,
  API_EDI_LINE_DISPLAY_PROFILE_SAVE,
} from '../../constants/apiConstants';

// ============================================================================
// Buyer line display profile service — Spec buyer-line-display-profile.
//
// Per-buyer config of which columns a line grid shows, in what order. A buyer
// profile stores presentation OVERRIDES ONLY ({ key, visible?, order?, label?,
// width? }); each column's intrinsic binding (value source, level, sortability)
// is code-defined server-side in LineDisplayColumnRegistry.
//
//   resolve → effective columns (registry baseline + overrides, deepest wins)
//             + per-column provenance. AUTHENTICATED — feeds the operator grid
//             AND the admin preview.
//   list    → admin faceted list of stored profiles (manage-gated).
//   save    → create/supersede the overrides at a scope (manage-gated).
//
// Scope is (tenantId, buyerPartyId?, supplierPartyId?); the BE builds scope_path.
// Editable scopes are tenant / buyer / buyer+supplier only (supplier needs a buyer).
// ============================================================================

interface ResultEnvelope<T> {
  succeeded: boolean;
  statusCode?: number;
  messages?: string[];
  data?: T;
}

/** The grid a profile targets. `packing_line` is the only kind shipped (the Packing List
 *  carton-contents cell reuses it); the others are reserved. */
export type LineDisplayGridKind = 'packing_line' | 'po_line' | 'carton_content';

/** A fully resolved display column: the registry binding + effective presentation + provenance.
 *  This is the wire shape `<ConfigLineGrid>` consumes. */
export interface ResolvedDisplayColumn {
  key: string;
  /** Value source — `field` (line property by name), `extra` (captured EDI code), `attrcode`, `computed`. */
  source: 'field' | 'extra' | 'attrcode' | 'computed';
  /** FE property name (field/attrcode), EDI code (extra), or compute-fn key (computed). */
  code?: string | null;
  /** Two-level prepack display level: `parent` (bundle row) | `child` (size-component sub-grid). */
  level: 'parent' | 'child';
  sortable: boolean;
  align?: 'right' | 'left' | null;
  label: string;
  order: number;
  visible: boolean;
  width?: number | null;
  /** Intrinsic value-formatter token applied at display (e.g. `pipe-label`), or null for the raw value. */
  format?: string | null;
  /** Whether the column belongs in a SINGLE CARTON's contents — a fact about the item in the carton.
   *  False for line-total quantities (Ordered/Packed/Balance) and line state (Line Status). Intrinsic,
   *  never a per-buyer override. Optional so an older response still deserializes; absent ⇒ treated as
   *  relevant, matching the server default. Spec line-display-carton-relevant-columns. */
  cartonRelevant?: boolean;
  /** Provenance: `baseline`, or the scope path that supplied the override. */
  sourceScope: string;
}

/**
 * Does this column belong in a CARTON's contents, on the registry's own terms?
 *
 * The INTRINSIC half of the rule only — it deliberately ignores `visible`, which is the buyer's
 * presentation choice. `cartonRelevant` says what a column MEANS (a per-carton fact vs a line total or a
 * workflow state); `computed` columns are excluded because they compute against a packing LINE, not a
 * carton-content row.
 *
 * Lives here, beside `ResolvedDisplayColumn`, so the carton-contents renderer and the profile editor
 * share ONE definition: the renderer uses it to decide, the editor uses it to explain. Two places
 * answering this question differently is how the same column ends up in one surface and not the other.
 */
export const isCartonContentColumn = (c: ResolvedDisplayColumn): boolean =>
  c.cartonRelevant !== false && c.source !== 'computed';

/** One editable per-buyer presentation override (null field = inherit the registry default). */
export interface DisplayColumnOverride {
  key: string;
  visible?: boolean | null;
  order?: number | null;
  label?: string | null;
  width?: number | null;
}

/** One admin faceted-list row (a stored profile at a scope). */
export interface LineDisplayProfileSummary {
  id: number;
  scopePath: string;
  scopeLevel: number;
  gridKind: string;
  columnCount: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  isActive: boolean;
}

/** Scope tuple shared by resolve / save requests. */
export interface LineDisplayScope {
  tenantId: number;
  buyerPartyId?: number | null;
  supplierPartyId?: number | null;
}

export interface ResolveLineDisplayResponse {
  gridKind: string;
  scopePath: string;
  columns: ResolvedDisplayColumn[];
}

export interface ListLineDisplayResponse {
  profiles: LineDisplayProfileSummary[];
}

export interface SaveLineDisplayRequest extends LineDisplayScope {
  gridKind: LineDisplayGridKind;
  columns: DisplayColumnOverride[];
}

export interface SaveLineDisplayResponse {
  id: number;
  scopePath: string;
}

/** Thrown by `save` carrying the BE `Result.messages` so the UI can render validation errors verbatim. */
export class LineDisplaySaveError extends Error {
  constructor(public readonly messages: string[]) {
    super(messages.join(', ') || 'Failed to save line display profile');
    this.name = 'LineDisplaySaveError';
  }
}

function scopeBody(scope: LineDisplayScope) {
  return {
    tenantId: scope.tenantId,
    buyerPartyId: scope.buyerPartyId ?? undefined,
    supplierPartyId: scope.supplierPartyId ?? undefined,
  };
}

export const lineDisplayProfileService = {
  async resolve(gridKind: LineDisplayGridKind, scope: LineDisplayScope): Promise<ResolveLineDisplayResponse> {
    try {
      const response = await apiClient.post<ResultEnvelope<ResolveLineDisplayResponse>>(
        API_EDI_LINE_DISPLAY_PROFILE_RESOLVE,
        { gridKind, ...scopeBody(scope) },
      );
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to resolve line display profile');
      }
      return response.data.data ?? { gridKind, scopePath: '', columns: [] };
    } catch (error) {
      logger.error('Failed to resolve line display profile', error);
      throw error;
    }
  },

  async list(
    tenantId: number,
    opts?: { scopePathPrefix?: string; gridKind?: LineDisplayGridKind; includeExpired?: boolean },
  ): Promise<ListLineDisplayResponse> {
    try {
      const response = await apiClient.post<ResultEnvelope<ListLineDisplayResponse>>(
        API_EDI_LINE_DISPLAY_PROFILE_LIST,
        {
          tenantId,
          scopePathPrefix: opts?.scopePathPrefix ?? undefined,
          gridKind: opts?.gridKind ?? undefined,
          includeExpired: opts?.includeExpired ?? false,
        },
      );
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to list line display profiles');
      }
      return response.data.data ?? { profiles: [] };
    } catch (error) {
      logger.error('Failed to list line display profiles', error);
      throw error;
    }
  },

  async save(request: SaveLineDisplayRequest): Promise<SaveLineDisplayResponse> {
    try {
      const response = await apiClient.post<ResultEnvelope<SaveLineDisplayResponse>>(
        API_EDI_LINE_DISPLAY_PROFILE_SAVE,
        {
          gridKind: request.gridKind,
          ...scopeBody(request),
          columns: request.columns,
        },
      );
      if (!response.data.succeeded) {
        throw new LineDisplaySaveError(response.data.messages ?? []);
      }
      return response.data.data ?? { id: 0, scopePath: '' };
    } catch (error) {
      if (error instanceof LineDisplaySaveError) throw error;
      throw new LineDisplaySaveError([getErrorMessage(error)]);
    }
  },
};
